/* eslint-disable dot-notation */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { doGet } from '../services/http';
import { errorHandler, getAsyncThunkMsg, prepareDataForFlatList } from '../services/Utils';

/**
 * Async thunk to fetch orders from API
 * @param {Object} params - Query parameters (page, limit, filters)
 * @returns {Promise<Object>} Orders data with pagination info
 */
export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async (params, { rejectWithValue }) => {
    try {
      console.log('fetchOrders params:', params);

      const response = await doGet('orders', params);
      return { data: response.data, ...params };
    } catch (err) {
      console.error('fetchOrders error:', err);

      errorHandler(err);

      // normalize error into serializable payload
      return rejectWithValue(getAsyncThunkMsg(err));
    }
  }
);

/**
 * Redux slice for orders list state
 * Manages order history with pagination, refresh, and load-more functionality
 */
const OrdersSlice = createSlice({
  name: 'orders',
  initialState: {
    orders: [],
    page: 1,
    status: 'idle',
    error: null,
    refreshing: false,
    loadingMore: false,
    limit: 16,
  },
  reducers: {
    /** Increments page for load more */
    handleLoadMore(state, action) {
      state.page = state.page + 1;
      state.loadingMore = true;
    },
    /** Enables pull-to-refresh control */
    enableRefreshControl(state, action) {
      state.page = 1;
      if (!action.payload?.retry) {
        state.refreshing = true;
      }
    },
    /** Disables pull-to-refresh control */
    disableRefreshControl(state, action) {
      state.refreshing = false;
    },
    /** Sets current page number */
    setPage(state, action) {
      state.page = action.payload;
    },
    /** Resets orders state to initial */
    resetData(state, action) {
      return OrdersSlice.getInitialState();
    },
    /** Updates order feedback (rating) after submission */
    updateOrderFeedback(state, action) {
      const index = state.orders.findIndex(
        (x) => x.order_id === action.payload?.order_id,
      );
      if (index !== -1) {
        state.orders[index]['feedback_given'] = action.payload?.feedback_given;
        state.orders[index]['rating'] = action.payload?.rating;
      }
    },
    /** Updates order status after cancellation */
    updateOrderCancellation(state, action) {
      const index = state.orders.findIndex(
        (x) => x.order_id === action.payload?.order_id,
      );
      if (index !== -1) {
        state.orders[index]['order_status'] = action.payload?.order_status;
        state.orders[index]['order_status_text'] =
          action.payload?.order_status_text;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOrders.pending, (state, action) => {
      if (!state.refreshing && state.page === 1) {
        state.status = 'loading';
      }
    })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        state.status = 'succeeded';

        const payload = action.payload?.data ?? [];
        const numColumns = Number(action.payload?.numColumns ?? 1);
        const page = Number(action.payload?.page ?? 1);
        const len = payload.length;

        // load-more flag
        state.loadingMore = len === state.limit;

        // refresh vs append
        if (state.refreshing) {
          state.orders = payload;
        } else if (len) {
          state.orders.push(...payload);
        }
        state.orders = prepareDataForFlatList({
          numColumns,
          key: 'order_id',
          page,
          data: state.orders,
        });
        state.refreshing = false;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action?.payload;
        state.refreshing = false;
      });
  },
});

export const {
  handleLoadMore,
  enableRefreshControl,
  disableRefreshControl,
  resetData,
  updateOrderFeedback,
  updateOrderCancellation,
  setPage,
} = OrdersSlice.actions;
export default OrdersSlice.reducer;
